/* Interfaces */
import { debounce } from '@utils/debounce';

interface ISearchHit {
  heading: string;
  url: string;
  preamble: string;
  lastUpdatedDate: string;
  lastUpdatedDateISO: string;
  hitType: string;
  isBestBet: boolean;
}

interface ISearchResult {
  searchHits: ISearchHit[];
  totalHits: number;
}

interface ITextContent {
  bestBetTag: string;
  noResults: string;
  resultFor: string;
  resultsFor: string;
  updated: string;
  showMore: string;
  showing: string;
  outOf: string;
}

interface IFilterType {
  count: number;
  displayName: string;
}

interface IFilters {
  facets: IFilterType[];
}

export const useSearchPage = () => {
  /* HTML elements */
  const searchPage = document.querySelector('.search-page') as HTMLElement;
  const searchForm = document.querySelector('.search-page__form') as HTMLFormElement;
  const searchInput = document.querySelector('.search-page__input') as HTMLInputElement;
  const searchFacetsSection = document.querySelector('.search-page__facets') as HTMLElement;
  const searchResultSection = document.querySelector('.search-page__result') as HTMLElement;
  const searchResultInfo = document.querySelector('.search-page__result-info') as HTMLParagraphElement;
  const translations = JSON.parse((document.querySelector('#text-translations') as HTMLScriptElement).textContent as string);

  /* Data */
  const { resultsToShow, resultsToAdd, currentLanguage } = searchForm.dataset;

  /* Text content (translated text) */

  const textContent: ITextContent = {
    noResults: translations.noResults as string,
    resultFor: translations.resultFor as string,
    resultsFor: translations.resultsFor as string,
    updated: translations.updated as string,
    showMore: translations.showMore as string,
    showing: translations.showing as string,
    outOf: translations.outOf as string,
    bestBetTag: translations.bestBetTag as string,
  };

  /* Global variables */
  let query: string | null = null;
  let apiSkipAmount = 0 as number;
  let apiTakeAmount = parseInt(resultsToShow!, 10) as number;
  let selectedFacet: string | null = null;
  const pageId = searchForm.dataset.pageid as string;

  /* Functions – Reset and clear DOM */
  const resetSearchResult = (): void => {
    renderWithoutResult();
  }

  const clearResultList = (): void => {
    const resultList: HTMLUListElement | null = document.querySelector('.search-page__result-list');
    const showMore: HTMLDivElement | null = document.querySelector('.search-page__show-more');

    if (resultList) {
      resultList.remove();
    }

    if (showMore) {
      showMore.remove();
    }
  }

  const clearFilters = (): void => {
    searchFacetsSection.classList.remove("search-page__facets--visible");
    searchFacetsSection.replaceChildren();
  };

  /* Functions – Get data from API */
  const getSearchResult = async(): Promise<ISearchResult | void> => {
    try {
      const url = `/api/search`;
      let params = `${url}?pageid=${pageId}&query=${query}&take=${apiTakeAmount}&skip=${apiSkipAmount}&language=${currentLanguage}`;

      if (!!selectedFacet) {
        params += `&facet=${selectedFacet}`;
      }

      const response = await fetch(params, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return result;
    } catch (error) {
      console.error(error);
    }
  }

  const getPossibleFiltersForQuery = async(): Promise<IFilters | void> => {
    let result;

    try {
      const response = await fetch(`/api/search/filters?pageid=${pageId}&query=${query}&language=${currentLanguage}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      result = await response.json();

      if (!response.ok) {
        console.error(response.statusText);
      }
    } catch (error) {
      console.error(error);
    }

    if (result) {
      return result;
    }

    return { facets: [] };
  };

  /* Functions – Render DOM */
  const renderFacetsFilters = (filters: IFilterType[]): void => {
    filters.forEach((facet: IFilterType) => {
      const facetLink = document.createElement('button') as HTMLButtonElement;
      facetLink.classList.add('search-page__facet-link');
      facetLink.textContent = `${facet.displayName} (${facet.count})`;
      facetLink.dataset.displayName = facet.displayName;

      // Add the facet link to the facets section
      searchFacetsSection.appendChild(facetLink);

      // Add event listener to each facet link
      facetLink.addEventListener('click', (e) => {
        // If this button is the first, this is the "All" button, skip the facet
        if (e.currentTarget === searchFacetsSection.firstChild) {
          selectedFacet = "";
        } else {
          let facetString = (e.currentTarget as HTMLButtonElement).dataset.displayName as string;
          selectedFacet = encodeURIComponent(facetString);
        }

        // Mark the selected facet
        markSelectedFacet((e.currentTarget as HTMLButtonElement).dataset.displayName as string);

        search().then(() => {
          const facet = !!selectedFacet ? `&facet=${selectedFacet}` : '';

          window.history.replaceState({}, '', `?q=${query}&take=${apiTakeAmount}${facet}`);
        });
      });
    });

    // Mark the selected facet (If selectedFacet is null, mark the first facet)
    filters.forEach(() => {
      let selectedFacetDecoded = selectedFacet ? decodeURIComponent(selectedFacet) : null;
      markSelectedFacet(selectedFacetDecoded || (searchFacetsSection.firstChild as HTMLButtonElement)?.dataset?.displayName?.toString() || "");
    });
  };
  
  const markSelectedFacet = (facet: string): void => {
    const facetLinks = searchFacetsSection.querySelectorAll('.search-page__facet-link');

    // Remove the selected class from all facet links
    facetLinks.forEach(facetElement => {
      facetElement.classList.remove('search-page__facet-link--selected');
    });

    // Add the selected class to the facet link that matches the selected facet
    facetLinks.forEach(facetElement => {
      if ((facetElement as HTMLButtonElement).dataset.displayName === facet) {
        facetElement.classList.add('search-page__facet-link--selected');
      }
    });
  }

  const renderResultItems = (searchHits: ISearchHit[], resultList: HTMLUListElement): void => {
    searchHits.forEach(item => {
      const resultItem = document.createElement('li') as HTMLLIElement;
      resultItem.classList.add('search-page__result-item');

      if (item.hitType === 'Media') {
        resultItem.classList.add('search-page__result-item--media');
      }

      const link = document.createElement('a') as HTMLAnchorElement;
      link.classList.add('search-page__result-item-link');
      link.setAttribute('href', item.url);
      resultItem.appendChild(link);

      if (item.hitType === 'Media') {
        link.classList.add('search-page__result-item-link--icon-document');
      }
      
      if (item.isBestBet) {
        const bestBet = document.createElement('span') as HTMLSpanElement;
        bestBet.classList.add('search-page__result-item-best-bet');
        bestBet.textContent = textContent.bestBetTag;
        link.appendChild(bestBet);
      }

      const heading = document.createElement('h3') as HTMLHeadingElement;
      heading.classList.add('search-page__result-item-heading');
      heading.textContent = item.heading;
      link.appendChild(heading);

      if (item.preamble) {
        const preamble = document.createElement('p') as HTMLParagraphElement;
        preamble.classList.add('search-page__result-item-preamble');
        preamble.innerHTML = item.preamble;
        link.appendChild(preamble);
      }

      const date = document.createElement('p') as HTMLParagraphElement;
      date.classList.add('search-page__result-item-date');
      link.appendChild(date);

      const time = document.createElement('time') as HTMLTimeElement;
      time.setAttribute('datetime', item.lastUpdatedDateISO);
      time.textContent = `${textContent.updated}: ${item.lastUpdatedDate}`;
      date.appendChild(time);

      resultList.appendChild(resultItem);
    });
  }

  const renderShowMoreButton = (): void => {
    const showMoreContainer = document.createElement('div');
    showMoreContainer.classList.add('search-page__show-more');

    const showMoreButton = document.createElement('button');
    showMoreButton.classList.add('search-page__show-more-button', 'button', 'button--secondary');
    showMoreButton.setAttribute('type', 'button');
    showMoreButton.textContent = textContent.showMore;

    showMoreButton.addEventListener('click', (event: MouseEvent) => {
      addMoreResultItems(showMoreButton);
    });

    showMoreContainer.appendChild(showMoreButton);
    searchResultSection.appendChild(showMoreContainer);
  }

  const renderResult = (searchResult: ISearchResult): void => {
    clearResultList();

    // Result info
    searchResultInfo.innerHTML = `${textContent.showing} <strong>${searchResult.searchHits.length}</strong> ${textContent.resultsFor} <em><strong>${query}</strong></em> ${textContent.outOf} <strong>${searchResult.totalHits}</strong>.`;

    if (searchResult.totalHits > 0) {
      const resultList = document.createElement('ul') as HTMLUListElement;
      resultList.classList.add('search-page__result-list');
      searchResultSection.appendChild(resultList);

      renderResultItems(searchResult.searchHits, resultList);

      if (searchResult.searchHits.length < searchResult.totalHits) {
        renderShowMoreButton();
      }
    }
  }

  const renderWithoutResult = (): void => {
    clearResultList();

    clearFilters();

    searchInput.value = '';
    searchResultInfo.textContent = '';
  }

  /* Functions - Search */
  const search = async(clearSelectedFacet = false): Promise<void> => {
    if (clearSelectedFacet) {
      selectedFacet = null;
    }

    toggleLoader(true);
    apiSkipAmount = 0;

    const searchResult = await getSearchResult();

    if (searchResult) {
      renderResult(searchResult);
      toggleLoader(false);
    }
  }

  const searchFilters = async(): Promise<void> => {
    const filters = await getPossibleFiltersForQuery();

    clearFilters();

    if (filters?.facets.length) {
      renderFacetsFilters(filters.facets);
    }
  };

  const searchFromQueryString = (): void => {
    const urlParams = new URLSearchParams(window.location.search) as URLSearchParams;
    const queryFromParam: string | null = urlParams.get('q');
    const takeFromParam: string | null = urlParams.get('take');
    const facetFromParam: string | null = urlParams.get('facet');

    query = queryFromParam;

    if (query) {
      query = query.trim();
      searchInput.value = query;
    } else {
      query = '';
    }

    if (takeFromParam && !isNaN(parseInt(takeFromParam, 10))) {
      apiTakeAmount = parseInt(takeFromParam, 10);
    }

    if (facetFromParam) {
      selectedFacet = encodeURIComponent(facetFromParam);
    }

    if (query.length > 0) {
      Promise.all([search(), searchFilters()]).then(() => {
        toggleResultInfo(true);
        toggleFacets(true)
      });
    } else {
      renderWithoutResult();
    }
  }

  const addMoreResultItems = async(showMoreButton: HTMLButtonElement): Promise<void> => {
    apiSkipAmount += apiTakeAmount;
    apiTakeAmount = parseInt(resultsToAdd!, 10);

    const searchResult = await getSearchResult();

    if (searchResult) {
      const resultList = document.querySelector('.search-page__result-list') as HTMLUListElement;
      const resultItemsCount = resultList.children.length as number;

      let takeAmount = resultItemsCount + parseInt(resultsToAdd!, 10);

      if (searchResult.totalHits < takeAmount) {
        takeAmount = searchResult.totalHits;
      }

      const facet = selectedFacet ? `&facet=${selectedFacet}` : '';

      window.history.replaceState({}, '', `?q=${query}&take=${takeAmount}${facet}`);

      renderResultItems(searchResult.searchHits, resultList);

      if (resultList.children.length === searchResult.totalHits) {
        showMoreButton.remove();
      }

      const resultItems = resultList.children as HTMLCollection;
      const firstAddedResultItem = resultItems[resultItemsCount] as HTMLLIElement;
      const firstAddedResultItemLink = firstAddedResultItem.querySelector('a') as HTMLAnchorElement;
      firstAddedResultItemLink.focus();
    }
  }

  const toggleLoader = (isVisible: boolean): void => {
    const loadingBackdrop = document.querySelector('.search-page__loading-backdrop') as HTMLDivElement;
    loadingBackdrop.classList.toggle('search-page__loading-backdrop--visible', isVisible);
  };

  const toggleFacets = (isVisible: boolean): void => {
    const facets = document.querySelector('.search-page__facets') as HTMLDivElement;
    facets.classList.toggle('search-page__facets--visible', isVisible);
  }

  const toggleResultInfo = (isVisible: boolean): void => {
    const resultInfo = document.querySelector('.search-page__result-info') as HTMLDivElement;
    resultInfo.classList.toggle('search-page__result-info--visible', isVisible);
  }

  /* Event listeners */

  // Event that happens if someone enters url with query string
  window.addEventListener('load', (): void => {
    if (window.location.search) {
      searchFromQueryString();
    }

    searchInput.focus();
  });

  // Event that happens when users browse through their browser history, e.g. use the back button.
  window.addEventListener('popstate', (): void => {
    if (window.location.search) {
      searchFromQueryString();
    } else {
      resetSearchResult();
    }

    searchInput.focus();
  });

  // Event that happens when user submits a search with the form
  searchForm.addEventListener('submit', (event: SubmitEvent): void => {
    event.preventDefault();

    query = searchInput.value.trim();
    apiSkipAmount = 0;
    apiTakeAmount = parseInt(resultsToShow!, 10);

    window.history.pushState({}, '', `?q=${query}`);

    if (query.length > 0) {
      Promise.all([search(true), searchFilters()]).then(() => {
        toggleResultInfo(true);
        toggleFacets(true)
      });
    } else {
      renderWithoutResult();
    }
  });

  // Scroll event to fix the search input position when fixed
  window.addEventListener("scroll", debounce(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    const headerHeight = document.querySelector('.header')?.clientHeight || 0;
    const searchFormHeight = searchForm.clientHeight || 0;

    if (scrollTop > headerHeight && !searchPage.classList.contains('search-page--search-input-fixed')) {
      searchPage.classList.add('search-page--search-input-fixed-loading');

      setTimeout(() => {
        // If the user has scrolled back to the top before the timeout completes, remove the class
        const currentScrollTop = window.scrollY || document.documentElement.scrollTop || 0;

        if (currentScrollTop < headerHeight) {
          searchPage.classList.remove('search-page--search-input-fixed-loading');
          searchPage.classList.remove('search-page--search-input-fixed');
          return;
        }

        // Otherwise, add the fixed class
        searchPage.classList.add('search-page--search-input-fixed');
        searchPage.classList.remove('search-page--search-input-fixed-loading');
      }, 1000);
    } else if(scrollTop < headerHeight && searchPage.classList.contains('search-page--search-input-fixed')) {
      searchPage.classList.remove('search-page--search-input-fixed');
    }
    
  }, 100));
};

useSearchPage();